import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  DEFAULT_DISPLAY_COUNTER_DATA,
  fetchCounterData,
  getCachedCounterData,
  setCachedCounterData,
  toDisplayCounterData,
  type DisplayCounterData,
} from '@vercount/core'

/**
 * SPA-aware Vercount hook.
 * Official @vercount/react only fires once on mount; we re-count on route changes
 * so page_pv tracks client-side navigations correctly.
 */
export function useVercount(): DisplayCounterData {
  const location = useLocation()
  const lastCountedUrl = useRef<string | null>(null)
  const [data, setData] = useState<DisplayCounterData>(DEFAULT_DISPLAY_COUNTER_DATA)

  useEffect(() => {
    let cancelled = false

    const cached = getCachedCounterData()
    if (cached) {
      const cachedDisplay = toDisplayCounterData(cached)
      setData({ ...cachedDisplay, pagePv: '0' })
    } else {
      setData(DEFAULT_DISPLAY_COUNTER_DATA)
    }

    const currentUrl = window.location.href
    if (lastCountedUrl.current === currentUrl) return
    lastCountedUrl.current = currentUrl

    fetchCounterData({
      url: currentUrl,
      onError: () => {
        if (lastCountedUrl.current === currentUrl) {
          lastCountedUrl.current = null
        }
      },
    }).then((fresh) => {
      if (cancelled || !fresh) return
      setCachedCounterData(fresh)
      setData(toDisplayCounterData(fresh))
    })

    return () => {
      cancelled = true
    }
  }, [location.pathname, location.search])

  return data
}
